import { useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { ISiloInfo, SiloShape } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { InfoGroupCard, type InfoGroupEntry } from '@/components/infoGroupCard/InfoGroupCard.tsx';
import {
  DeviceInfoOverview,
  deviceInfoOverviewGridColumns,
} from '@/components/informationModal/DeviceInfoOverview.tsx';
import type { DeviceSummary } from '@/types';
import { responsive } from '@/utils';

import { useSiloShape } from './siloInfoFields/useSiloShape.tsx';

type InfoFieldsProps = {
  siloInfo: ISiloInfo;
};

export const SiloInfoFields = ({ siloInfo }: InfoFieldsProps) => {
  const { t } = useTranslation(['common', 'bargeControl', 'deviceConfig']);

  const { siloIndex, siloShape, dimensions, density, content, alarmLevels, sensorRange, level } =
    useSiloShape(siloInfo);

  const overviewExtraEntries = useMemo(
    (): InfoGroupEntry[] => [
      { id: 'siloIndex', label: t('bargeControl:silo.index'), value: siloIndex },
    ],
    [t, siloIndex]
  );

  const measurementEntries = useMemo(
    (): InfoGroupEntry[] => [
      {
        id: 'level',
        label: t('bargeControl:tank.level'),
        value: level?.height,
        unit: 'm',
        withSpaceToUnit: true,
      },
      {
        id: 'volume',
        label: t('bargeControl:tank.volume'),
        value: level?.volume,
        unit: 'm\u00B3',
        withSpaceToUnit: true,
      },
      {
        id: 'density',
        label: t('bargeControl:density'),
        value: density,
        unit: 'kg/m\u00B3',
        withSpaceToUnit: true,
      },
      { id: 'content', label: t('bargeControl:silo.content'), value: content },
    ],
    [content, density, level?.height, level?.volume, t]
  );

  const shapeEntries = useMemo((): InfoGroupEntry[] => {
    const list: InfoGroupEntry[] = [
      { id: 'shape', label: t('bargeControl:silo.shape'), value: siloShape },
    ];
    if (!dimensions) {
      return list;
    }
    if (siloShape === SiloShape.Circular) {
      list.push(
        {
          id: 'diameter',
          label: t('bargeControl:silo.dimensions.diameter'),
          value: dimensions.siloDiameter,
          unit: 'm',
          withSpaceToUnit: true,
        },
        {
          id: 'totalHeight',
          label: t('bargeControl:silo.dimensions.totalHeight'),
          value: dimensions.siloTotalHeight,
          unit: 'm',
          withSpaceToUnit: true,
        },
        {
          id: 'coneHeight',
          label: t('bargeControl:silo.dimensions.coneHeight'),
          value: dimensions.siloConeHeight,
          unit: 'm',
          withSpaceToUnit: true,
        }
      );
    } else {
      list.push(
        {
          id: 'width',
          label: t('bargeControl:silo.dimensions.width'),
          value: dimensions.siloWidth,
          unit: 'm',
          withSpaceToUnit: true,
        },
        {
          id: 'length',
          label: t('bargeControl:silo.dimensions.length'),
          value: dimensions.siloLength,
          unit: 'm',
          withSpaceToUnit: true,
        },
        {
          id: 'totalHeight',
          label: t('bargeControl:silo.dimensions.totalHeight'),
          value: dimensions.siloTotalHeight,
          unit: 'm',
          withSpaceToUnit: true,
        },
        {
          id: 'coneHeight',
          label: t('bargeControl:silo.dimensions.coneHeight'),
          value: dimensions.siloConeHeight,
          unit: 'm',
          withSpaceToUnit: true,
        }
      );
    }
    return list;
  }, [dimensions, siloShape, t]);

  const rangeEntries = useMemo(
    (): InfoGroupEntry[] => [
      {
        id: 'alarmLevels',
        label: t('bargeControl:configModal.alarmLevels'),
        value: alarmLevels != null ? `${alarmLevels.low} m / ${alarmLevels.high} m` : undefined,
      },
      {
        id: 'sensorRange',
        label: t('bargeControl:configModal.sensorLevels'),
        value: sensorRange != null ? `${sensorRange.min} / ${sensorRange.max}` : undefined,
      },
    ],
    [alarmLevels, sensorRange, t]
  );

  return (
    <FlexAligner direction='column' gap={responsive.spacing['large']}>
      <DeviceInfoOverview device={siloInfo as DeviceSummary} extraEntries={overviewExtraEntries} />
      <InfoGroupCard entries={measurementEntries} columns={deviceInfoOverviewGridColumns} />
      <InfoGroupCard entries={shapeEntries} columns={deviceInfoOverviewGridColumns} />
      <InfoGroupCard entries={rangeEntries} columns={deviceInfoOverviewGridColumns} />
    </FlexAligner>
  );
};
